// Grupos de camadas: seleção, caixa envolvente, escala proporcional e animação própria do grupo.
//
// Um grupo não é um objeto separado: é o nome em `el.grupo` compartilhado pelas camadas.
// A animação do grupo fica copiada em cada membro (`el.grupoAnim`), assim ela viaja junto
// com os elementos (salvamento, WebSocket, operador e saída) sem mudar o backend.

export const GROUP_PREFIX = "grp:";
export const groupSelId = (name) => `${GROUP_PREFIX}${name}`;
export const isGroupSel = (sel) => typeof sel === "string" && sel.startsWith(GROUP_PREFIX);
export const groupNameOf = (sel) => (isGroupSel(sel) ? sel.slice(GROUP_PREFIX.length) : null);

const GROUP_COLORS = ["#f59e0b", "#10b981", "#a78bfa", "#f472b6", "#22d3ee", "#fb923c", "#a3e635", "#fb7185"];
/** Uma cor por grupo, pela ordem alfabética do nome (não muda ao reordenar camadas). */
export function groupColorMap(elements) {
  const names = [...new Set((elements || []).map((e) => e.grupo).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  return Object.fromEntries(names.map((n, i) => [n, GROUP_COLORS[i % GROUP_COLORS.length]]));
}

export const membersOf = (elements, name) => (elements || []).filter((e) => e.grupo === name);

/** Caixa que envolve todas as camadas do grupo (coordenadas do overlay). */
export function groupBBox(members) {
  if (!members.length) return { x: 0, y: 0, w: 0, h: 0 };
  const x1 = Math.min(...members.map((m) => m.x || 0));
  const y1 = Math.min(...members.map((m) => m.y || 0));
  const x2 = Math.max(...members.map((m) => (m.x || 0) + (m.largura || 0)));
  const y2 = Math.max(...members.map((m) => (m.y || 0) + (m.altura || 0)));
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

/** Animação de entrada/saída do grupo (independente das animações de cada camada). */
export function groupAnimOf(members) {
  const m = members.find((x) => x.grupoAnim);
  return m ? m.grupoAnim : null;
}
export function hasGroupAnim(anim) {
  if (!anim) return false;
  const e = anim.entrada?.efeito, s = anim.saida?.efeito;
  return (e && e !== "none") || (s && s !== "none");
}

/** Patches para mover todas as camadas do grupo. */
export function movePatches(members, dx, dy) {
  return Object.fromEntries(members.map((m) => [m.id, { x: Math.round((m.x || 0) + dx), y: Math.round((m.y || 0) + dy) }]));
}

const scaleNum = (v, s) => (typeof v === "number" ? Math.max(0, Math.round(v * s * 100) / 100) : v);

/** Patches para redimensionar o grupo proporcionalmente (posição, tamanho, fonte, bordas). */
export function scalePatches(members, from, to) {
  const s = from.w ? to.w / from.w : 1;
  const patches = {};
  for (const m of members) {
    const estilo = { ...(m.estilo || {}) };
    for (const k of ["fontSize", "padding", "borderRadius", "strokeWidth", "letterSpacing", "shadowX", "shadowY", "shadowBlur", "cornerRadius"]) {
      if (typeof estilo[k] === "number") estilo[k] = k === "fontSize" ? Math.max(6, Math.round(estilo[k] * s)) : scaleNum(estilo[k], s);
    }
    patches[m.id] = {
      x: Math.round(to.x + ((m.x || 0) - from.x) * s),
      y: Math.round(to.y + ((m.y || 0) - from.y) * s),
      largura: Math.max(4, Math.round((m.largura || 0) * s)),
      altura: Math.max(4, Math.round((m.altura || 0) * s)),
      estilo,
    };
  }
  return patches;
}

/** Topo da pilha (maior zIndex) entre as camadas do grupo que contêm o ponto. */
export function memberAt(members, px, py) {
  const hits = members.filter((m) => px >= m.x && px <= m.x + m.largura && py >= m.y && py <= m.y + m.altura);
  hits.sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
  return hits[0] || null;
}
